import React, { useState, useCallback, useEffect } from 'react';
import { User, Building, FileText, AlertTriangle, ListChecks } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ToastContainer, useToast } from '../../components/shared/Toast';
import Button from '../../components/shared/Button';
import Input from '../../components/shared/Input';
import ModalShell from '../../components/shared/ModalShell';
import { authService } from '../../services/authService';
import VendorLocationManagerCard from '../../components/vendor/account/VendorLocationManagerCard';
import {
  ProfileInformationTab,
  BusinessProfileTab,
  TermsConditionsTab
} from '../../components/vendor/profile';
import { VendorPageHeader, VendorGuideCallout } from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';

const VendorAccount = () => {
  const { user, updateUser, logout } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = ['profile', 'business', 'terms'].includes(searchParams.get('tab'))
    ? searchParams.get('tab')
    : 'profile';
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (['profile', 'business', 'terms'].includes(tab) && tab !== activeTab) {
      setActiveTab(tab);
    }
  }, [searchParams, activeTab]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams(tabId === 'profile' ? {} : { tab: tabId }, { replace: true });
  };

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);

  const deletionRequiresPassword = user?.deletion_requires_password !== false;

  const closeDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setDeletePassword('');
    setDeleteConfirmText('');
    setDeleteReason('');
  }, []);

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Confirmation required', 'Type DELETE (all caps) to confirm.');
      return;
    }
    if (deletionRequiresPassword && !deletePassword.trim()) {
      toast.error('Password required', 'Enter your account password.');
      return;
    }
    setDeleting(true);
    try {
      await authService.deleteAccount({
        ...(deletionRequiresPassword ? { password: deletePassword } : {}),
        reason: deleteReason.trim() || undefined,
      });
      toast.success('Account closed', 'Your account has been deleted.');
      localStorage.removeItem('token');
      await logout();
      navigate('/', { replace: true });
    } catch (err) {
      const data = err.response?.data;
      const msg =
        data?.message ||
        data?.errors?.password?.[0] ||
        err.message ||
        'Could not delete account.';
      toast.error('Cannot delete account', msg);
    } finally {
      setDeleting(false);
      closeDeleteModal();
    }
  };

  const tabs = [
    { id: 'profile', name: 'Profile Information', icon: User },
    { id: 'business', name: 'Business Profile', icon: Building },
    { id: 'terms', name: 'Terms & Conditions', icon: FileText }
  ];

  return (
    <div className="space-y-5">
      <VendorPageHeader
        title="Account"
        subtitle="Edit personal details, business profile, logo, and operating location. Changes save on each section."
        actions={
          <Link
            to="/vendor/profile"
            className="inline-flex items-center gap-1.5 self-start text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
          >
            View profile preview
          </Link>
        }
      />

      <VendorGuideCallout
        title={VENDOR_PAGE_GUIDES.account.title}
        steps={VENDOR_PAGE_GUIDES.account.steps}
        icon={ListChecks}
      />

      <VendorLocationManagerCard onNavigateToBusinessTab={() => handleTabChange('business')} />

      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
        <div className="border-b border-gray-200 dark:border-gray-800 px-4 py-3 sm:px-5 overflow-x-auto">
          <div
            className="inline-flex flex-wrap rounded-xl bg-gray-100 dark:bg-gray-800 p-1 border border-gray-200/80 dark:border-gray-700 min-w-max"
            role="tablist"
            aria-label="Account sections"
          >
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => handleTabChange(tab.id)}
                  className={`inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-3.5 py-2 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap transition-all ${
                    isActive
                      ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-50 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-4 sm:p-6 lg:p-8">
          {activeTab === 'profile' && <ProfileInformationTab user={user} updateUser={updateUser} />}
          {activeTab === 'business' && <BusinessProfileTab user={user} updateUser={updateUser} />}
          {activeTab === 'terms' && <TermsConditionsTab user={user} updateUser={updateUser} />}
        </div>
      </div>

      <div className="rounded-2xl border border-red-200 dark:border-red-800/50 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-6">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400">
            <AlertTriangle className="w-4 h-4" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Danger zone</h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Permanently close your vendor account. You cannot undo this. Active bookings may block
              deletion until they are completed or cancelled.
            </p>
            <div className="mt-4">
              <Button
                type="button"
                variant="outline"
                className="!rounded-xl border-red-300 dark:border-red-800 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/40"
                onClick={() => setDeleteModalOpen(true)}
              >
                Delete account
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ModalShell
        isOpen={deleteModalOpen}
        onClose={() => !deleting && closeDeleteModal()}
        labelledBy="delete-account-title"
        panelClassName="max-w-lg"
      >
            <div
              className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-lg w-full p-6 border border-gray-200 dark:border-gray-700"
              role="dialog"
              aria-modal="true"
              aria-labelledby="delete-account-title"
            >
              <h2 id="delete-account-title" className="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-2">
                Delete your account
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {deletionRequiresPassword ? (
                  <>
                    This will close your account and sign you out. Enter your password and type{' '}
                    <span className="font-mono font-semibold">DELETE</span> to confirm.
                  </>
                ) : (
                  <>
                    You signed in without a password (for example with Google). Type{' '}
                    <span className="font-mono font-semibold">DELETE</span> to confirm you want to close this account.
                  </>
                )}
              </p>
              <form onSubmit={handleDeleteAccount} className="space-y-4">
                {deletionRequiresPassword && (
                  <div>
                    <label htmlFor="delete-account-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Password
                    </label>
                    <Input
                      id="delete-account-password"
                      type="password"
                      autoComplete="current-password"
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                      disabled={deleting}
                      placeholder="Your account password"
                    />
                  </div>
                )}
                <div>
                  <label htmlFor="delete-account-reason" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Reason <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <Input
                    id="delete-account-reason"
                    type="text"
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                    disabled={deleting}
                    placeholder="Tell us why you are leaving"
                  />
                </div>
                <div>
                  <label htmlFor="delete-account-confirm" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Type DELETE to confirm
                  </label>
                  <Input
                    id="delete-account-confirm"
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    disabled={deleting}
                    placeholder="DELETE"
                    autoComplete="off"
                  />
                </div>
                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={closeDeleteModal} disabled={deleting}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="danger" disabled={deleting} loading={deleting}>
                    Delete my account
                  </Button>
                </div>
              </form>
            </div>
      </ModalShell>

      {/* Toast Container */}
      <ToastContainer 
        toasts={toast.toasts} 
        position="top-right"
        onRemove={toast.removeToast}
      />
    </div>
  );
};

export default VendorAccount;

